import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaArrowLeft, FaEye, FaChevronDown, FaChevronUp } from "react-icons/fa";
import DashboardLayout from "./DashboardLayout";
import SortableTable from "../../Pages/Components/SortableTable";
import SearchBar from "../../Pages/Components/SearchBar";
import axiosInstance from "../../api/axiosInstance";
import { getCustomerWiseRedemptionSummary } from "../../redux/actions/orderActions";

// ─── helpers ────────────────────────────────────────────────────────────────
const fmtDateTime = (d) => (d ? dayjs(d).format("DD MMM YYYY, hh:mm A") : "—");

// Same cell / row classes as RedeemedStatistics so the table looks identical
const TD = "px-2 py-4 border-b-gray-400";
const TR = "hover:bg-amber-50 hover:text-gray-950 hover:font-semibold";

const Thumb = ({ src, alt }) => {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return <span className="text-gray-400">—</span>;
  return (
    <a href={src} target="_blank" rel="noreferrer" title="Open full size">
      <img
        src={src}
        alt={alt}
        onError={() => setFailed(true)}
        className="h-12 w-12 rounded-md border border-gray-300 bg-white object-cover"
      />
    </a>
  );
};

const ViewToggle = ({ open, onClick }) => (
  <button
    onClick={onClick}
    aria-expanded={open}
    className="flex items-center gap-2 text-amber-600 hover:text-amber-700 font-medium"
  >
    <FaEye /> {open ? "Hide" : "View"}
    {open ? <FaChevronUp size={11} /> : <FaChevronDown size={11} />}
  </button>
);

// ─── dropdown content ───────────────────────────────────────────────────────
const PLACEHOLDER =
  'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="150"%3E%3Crect fill="%23ddd" width="200" height="150"/%3E%3C/svg%3E';

const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith("http")) return imagePath;
  const base = axiosInstance.defaults.baseURL || "";
  const separator = base.endsWith("/") || imagePath.startsWith("/") ? "" : "/";
  return `${base}${separator}${imagePath.startsWith("/") ? imagePath.slice(1) : imagePath}`;
};

const VerifyImage = ({ label, src, alt }) => (
  <div>
    <p className="text-xs text-gray-600 mb-2">{label}</p>
    <img
      src={getImageUrl(src)}
      alt={alt}
      className="max-w-xs h-32 rounded border border-gray-200 object-cover"
      onError={(e) => {
        e.target.onerror = null;
        e.target.src = PLACEHOLDER;
      }}
    />
  </div>
);

// Shared QR list table (Redeemed + Accumulated dropdowns).
// The Scheme column only appears when the API sends a scheme for the QR codes.
const QrCodesTable = ({ qrCodes }) => {
  const showScheme = qrCodes.some((q) => q.scheme);
  const thCls =
    "border border-gray-300 px-4 py-2 text-xs font-semibold text-gray-600";
  const tdCls = "border border-gray-300 px-4 py-2 text-xs";
  return (
    <div className="overflow-x-auto">
      <table className="w-full bg-white border border-gray-200 rounded">
        <thead className="bg-gray-100">
          <tr>
            <th className={`${thCls} text-left`}>QR ID</th>
            {showScheme && <th className={`${thCls} text-left`}>Scheme</th>}
            <th className={`${thCls} text-center`}>Status</th>
            <th className={`${thCls} text-left`}>Created</th>
          </tr>
        </thead>
        <tbody>
          {qrCodes.map((qr, qrIdx) => (
            <tr key={qrIdx} className="border-t hover:bg-gray-50">
              <td className={`${tdCls} font-mono text-gray-900`}>{qr.qr_id}</td>
              {showScheme && (
                <td className={tdCls}>
                  {qr.scheme ? (
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                      {qr.scheme}
                    </span>
                  ) : (
                    "-"
                  )}
                </td>
              )}
              <td className={`${tdCls} text-center`}>
                <span
                  className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                    qr.status === "Redeemed"
                      ? "bg-blue-100 text-blue-800"
                      : "bg-yellow-100 text-yellow-800"
                  }`}
                >
                  {qr.status}
                </span>
              </td>
              <td className={`${tdCls} text-gray-600`}>
                {fmtDateTime(qr.time)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const RedeemedDetails = ({ row, detail, onRetry }) => {
  const loading = !detail || detail.loading;
  // The detail response holds all of this customer's gifts; pick this redemption
  const full = detail?.customer?.gifts?.find(
    (g) => g.redemption_id === row.gift.redemption_id,
  );
  const qrCodes = full?.qr_codes || [];

  return (
    <div className="ml-8 space-y-6">
      {detail?.error ? (
        <div className="text-sm text-red-500">
          {detail.error}{" "}
          <button
            onClick={onRetry}
            className="ml-2 font-medium text-amber-600 underline"
          >
            Retry
          </button>
        </div>
      ) : loading ? (
        <p className="text-sm text-gray-500">Loading details...</p>
      ) : (
        <>
          <div>
            <h4 className="text-sm font-bold text-gray-700 mb-3">
              Customer Verification
            </h4>
            {full?.customer_sign || full?.customer_selfie ? (
              <div className="grid grid-cols-2 gap-4">
                {full.customer_sign && (
                  <VerifyImage
                    label="Signature"
                    src={full.customer_sign}
                    alt="Customer Signature"
                  />
                )}
                {full.customer_selfie && (
                  <VerifyImage
                    label="Selfie"
                    src={full.customer_selfie}
                    alt="Customer Selfie"
                  />
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                No signature or selfie available
              </p>
            )}
          </div>

          <div>
            <h4 className="text-sm font-bold text-gray-700 mb-3">
              QR Codes Used ({qrCodes.length})
            </h4>
            {qrCodes.length > 0 ? (
              <QrCodesTable qrCodes={qrCodes} />
            ) : (
              <p className="text-sm text-gray-500">No QR codes found</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const AccumulatedDetails = ({ detail, onRetry }) => {
  const loading = !detail || detail.loading;
  // The customer-level list has every QR the customer scanned, redeemed ones
  // included. The Accumulated card should only list the ones not yet redeemed.
  const qrCodes = (detail?.customer?.qr_codes || []).filter(
    (q) => String(q.status).toLowerCase() !== "redeemed",
  );

  return (
    <div>
      <h4 className="text-sm font-bold text-gray-700 mb-3">
        Accumulated QR Codes
        {!loading && !detail?.error ? ` (${qrCodes.length})` : ""}
      </h4>
      {detail?.error ? (
        <div className="text-sm text-red-500">
          {detail.error}{" "}
          <button
            onClick={onRetry}
            className="ml-2 font-medium text-amber-600 underline"
          >
            Retry
          </button>
        </div>
      ) : loading ? (
        <p className="text-sm text-gray-500">Loading QR codes...</p>
      ) : qrCodes.length > 0 ? (
        <QrCodesTable qrCodes={qrCodes} />
      ) : (
        <p className="text-sm text-gray-500">No accumulated QR codes found</p>
      )}
    </div>
  );
};

// ─── page ───────────────────────────────────────────────────────────────────
const RedeemedSummary = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { cardType = "redeemed" } = useParams();
  const { userInfo } = useSelector((state) => state.auth);

  const [searchValue, setSearchValue] = useState("");
  const [selectedSalesman, setSelectedSalesman] = useState(""); // "" = all salesmen
  const [dateRange, setDateRange] = useState(
    location.state?.dateRange || [null, null],
  );
  const [startDate, endDate] = dateRange;
  const startStr = startDate ? dayjs(startDate).format("YYYY-MM-DD") : null;
  const endStr = endDate ? dayjs(endDate).format("YYYY-MM-DD") : null;
  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    const res = await dispatch(
      getCustomerWiseRedemptionSummary(
        { ...filterParams, is_download: true },
        userInfo.access_token,
      ),
    );
    if (res.success && res.data?.download_url) {
      window.open(res.data.download_url, "_blank");
    } else {
      alert(res.error || "Export failed — no file returned");
    }
    setExporting(false);
  };
  // Filters sent to the API (null values are skipped by axios)
  const filterParams = {
    start_date: startStr,
    end_date: endStr,
    created_by: selectedSalesman || null,
  };
  const [expandedKey, setExpandedKey] = useState(null); // one open dropdown at a time

  const toggleRow = (key) =>
    setExpandedKey((prev) => (prev === key ? null : key));

  // Per-customer detail (QR list, selfie, signature) fetched when View is clicked
  const [details, setDetails] = useState({}); // { [customerId]: { loading, error, customer } }

  // Called every time a View dropdown is opened, so the API is hit on each click
  const loadDetail = async (customerId) => {
    setDetails((prev) => ({
      ...prev,
      [customerId]: { loading: true, error: null, customer: null },
    }));
    // same API as the list, just with ?customer_id=<id>
    const res = await dispatch(
      getCustomerWiseRedemptionSummary(
        { customer_id: customerId, ...filterParams },
        userInfo.access_token,
      ),
    );
    setDetails((prev) => ({
      ...prev,
      [customerId]: res?.success
        ? { loading: false, error: null, customer: res.customer }
        : {
            loading: false,
            error: res?.error || "Failed to load details",
            customer: null,
          },
    }));
  };

  const toggleRedeemed = (row) => {
    const key = `r-${row.redemption_id}`;
    const opening = expandedKey !== key;
    toggleRow(key);
    if (opening) loadDetail(row.customer.customer_id);
  };

  const toggleAccumulated = (row) => {
    const key = `c-${row.customer_id}`;
    const opening = expandedKey !== key;
    toggleRow(key);
    if (opening) loadDetail(row.customer_id);
  };

  const {
    loading = false,
    data: customers = [],
    error = null,
  } = useSelector((state) => state.customerRedemptionSummary || {});

  useEffect(() => {
    if (!userInfo?.access_token) {
      navigate("/login");
      return;
    }
    if (startStr && !endStr) return; // range half-picked, wait for the end date
    dispatch(
      getCustomerWiseRedemptionSummary(
        {
          start_date: startStr,
          end_date: endStr,
          created_by: selectedSalesman || null,
        },
        userInfo.access_token,
      ),
    );
    setExpandedKey(null);
  }, [dispatch, userInfo, navigate, startStr, endStr, selectedSalesman]);

  // ── salesman list for the filter (dealer is not counted as a salesman) ────
  const salesmenList = useMemo(() => {
    const map = new Map();
    customers.forEach((c) => {
      const dealerId = c.dealer?.user_id;
      const add = (u) => {
        if (u?.user_id && u.user_id !== dealerId) map.set(u.user_id, u.name);
      };
      add(c.created_by);
      (c.gifts || []).forEach((g) => add(g.redeemed_by));
    });
    return [...map]
      .map(([user_id, name]) => ({ user_id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [customers]);

  // The API only returns the filtered salesman's data, so remember every salesman
  // we have seen; otherwise the dropdown would shrink to the selected one.
  const [salesmanOptions, setSalesmanOptions] = useState([]);
  useEffect(() => {
    if (!salesmenList.length) return;
    setSalesmanOptions((prev) => {
      const map = new Map(prev.map((sm) => [sm.user_id, sm]));
      salesmenList.forEach((sm) => map.set(sm.user_id, sm));
      return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
    });
  }, [salesmenList]);

  // ── summary cards (reflect whatever the API returned for the filters) ─────
  const summary = useMemo(() => {
    let accumulated = 0;
    let redeemed = 0;
    let gifts = 0;
    customers.forEach((c) => {
      accumulated += c.accumulated_qr_count || 0;
      redeemed += c.redeemed_qr_count || 0;
      gifts += (c.gifts || []).length;
    });
    return {
      salesmen: salesmenList.length,
      customers: customers.length,
      accumulated,
      redeemed,
      gifts,
      scanned: accumulated + redeemed,
    };
  }, [customers, salesmenList]);

  const search = searchValue.trim().toLowerCase();
  const matches = (...parts) =>
    !search || parts.join(" ").toLowerCase().includes(search);

  // ── Redeemed: one row per redemption, ordered salesman-wise ───────────────
  const redeemedRows = useMemo(() => {
    const rows = [];
    customers.forEach((customer) => {
      (customer.gifts || []).forEach((gift) => {
        const salesman = gift.redeemed_by || {
          user_id: "unknown",
          name: "Unknown",
        };
        if (selectedSalesman && salesman.user_id !== selectedSalesman) return;
        if (
          !matches(
            salesman.name,
            customer.customer_name,
            customer.customer_code,
            customer.customer_mobile,
            customer.firm_name,
            gift.gift_name,
          )
        )
          return;
        rows.push({
          redemption_id: gift.redemption_id,
          salesman_name: salesman.name,
          customer_name: customer.customer_name,
          customer_code: customer.customer_code,
          gift_name: gift.gift_name,
          qr_count: gift.qr_count,
          gift_image: gift.gift_image,
          created_at: gift.created_at,
          customer,
          gift,
          salesman,
        });
      });
    });
    return rows.sort(
      (a, b) =>
        a.salesman_name.localeCompare(b.salesman_name) ||
        new Date(b.created_at) - new Date(a.created_at),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, selectedSalesman, search]);

  // ── Customers / Accumulated / Scanned: customer rows, filtered by creator ─
  const customerRows = useMemo(() => {
    return customers
      .filter((c) => {
        if (cardType === "accumulated" && !(c.accumulated_qr_count > 0))
          return false;
        if (
          cardType === "scanned" &&
          !(c.accumulated_qr_count + c.redeemed_qr_count > 0)
        )
          return false;
        return matches(
          c.customer_name,
          c.customer_code,
          c.customer_mobile,
          c.firm_name,
          c.customer_city,
        );
      })
      .map((c) => ({
        customer_id: c.customer_id,
        customer_name: c.customer_name,
        customer_code: c.customer_code,
        customer_mobile: c.customer_mobile,
        firm_name: c.firm_name,
        customer_city: c.customer_city,
        created_by_name: c.created_by?.name || "—",
        accumulated_qr_count: c.accumulated_qr_count,
        redeemed_qr_count: c.redeemed_qr_count,
        customer: c,
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, cardType, search]);

  // ── Salesmen: one row per salesman ────────────────────────────────────────
  const salesmanRows = useMemo(() => {
    return salesmenList
      .filter((s) => matches(s.name))
      .map((s) => {
        let redemptions = 0;
        let qrRedeemed = 0;
        customers.forEach((c) =>
          (c.gifts || []).forEach((g) => {
            if (g.redeemed_by?.user_id === s.user_id) {
              redemptions += 1;
              qrRedeemed += g.qr_count || 0;
            }
          }),
        );
        return {
          user_id: s.user_id,
          salesman_name: s.name,
          customers_count: customers.filter(
            (c) => c.created_by?.user_id === s.user_id,
          ).length,
          redemption_count: redemptions,
          qr_redeemed: qrRedeemed,
        };
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, salesmenList, search]);

  // ── row renderers (same SortableTable; View opens a dropdown row below) ───
  const renderRedeemedRow = (row) => {
    const key = `r-${row.redemption_id}`;
    const open = expandedKey === key;
    return (
      <React.Fragment key={key}>
        <tr className={`${TR} ${open ? "bg-amber-50" : ""}`}>
          <td className={TD}>{row.salesman_name}</td>
          <td className={TD}>{row.customer_name}</td>
          <td className={TD}>{row.customer_code}</td>
          <td className={TD}>{row.gift_name}</td>
          <td className={`${TD} font-semibold text-pink-600`}>
            {row.qr_count}
          </td>
          <td className={TD}>
            <Thumb src={row.gift_image} alt={row.gift_name} />
          </td>
          <td className={TD}>{fmtDateTime(row.created_at)}</td>
          <td className={TD}>
            <ViewToggle open={open} onClick={() => toggleRedeemed(row)} />
          </td>
        </tr>
        {open && (
          <tr>
            <td colSpan={8} className="bg-gray-50 px-4 py-4">
              <RedeemedDetails
                row={row}
                detail={details[row.customer.customer_id]}
                onRetry={() => loadDetail(row.customer.customer_id)}
              />
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  const renderCustomerRow = (row) => {
    const expandable = cardType === "accumulated";
    const key = `c-${row.customer_id}`;
    const open = expandable && expandedKey === key;
    return (
      <React.Fragment key={key}>
        <tr className={`${TR} ${open ? "bg-amber-50" : ""}`}>
          <td className={TD}>{row.customer_name}</td>
          {expandable && <td className={TD}>{row.customer_code}</td>}
          <td className={TD}>{row.customer_mobile}</td>
          <td className={TD}>{row.firm_name}</td>
          <td className={TD}>{row.customer_city}</td>
          <td className={TD}>{row.created_by_name}</td>
          <td className={TD}>{row.accumulated_qr_count}</td>
          <td className={`${TD} font-semibold text-pink-600`}>
            {row.redeemed_qr_count}
          </td>
          {expandable && (
            <td className={TD}>
              <ViewToggle open={open} onClick={() => toggleAccumulated(row)} />
            </td>
          )}
        </tr>
        {open && (
          <tr>
            <td colSpan={9} className="bg-amber-50 px-4 py-4 border-b-gray-400">
              <AccumulatedDetails
                detail={details[row.customer_id]}
                onRetry={() => loadDetail(row.customer_id)}
              />
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  const renderSalesmanRow = (row) => (
    <tr key={row.user_id} className={TR}>
      <td className={TD}>{row.salesman_name}</td>
      <td className={TD}>{row.customers_count}</td>
      <td className={TD}>{row.redemption_count}</td>
      <td className={`${TD} font-semibold text-pink-600`}>{row.qr_redeemed}</td>
    </tr>
  );

  const customerColumns = [
    { label: "Customer", key: "customer_name" },
    ...(cardType === "accumulated"
      ? [{ label: "Customer Code", key: "customer_code" }]
      : []),
    { label: "Mobile", key: "customer_mobile" },
    { label: "Firm", key: "firm_name" },
    { label: "City", key: "customer_city" },
    { label: "Created By", key: "created_by_name" },
    { label: "Accumulated", key: "accumulated_qr_count" },
    { label: "Redeemed", key: "redeemed_qr_count" },
    ...(cardType === "accumulated"
      ? [{ label: "Actions", key: "actions" }]
      : []),
  ];

  const tableConfig = {
    redeemed: {
      columns: [
        { label: "Salesman", key: "salesman_name" },
        { label: "Customer", key: "customer_name" },
        { label: "Customer Code", key: "customer_code" },
        { label: "Gift", key: "gift_name" },
        { label: "QR Count", key: "qr_count" },
        { label: "Gift Image", key: "gift_image" },
        { label: "Redeemed On", key: "created_at" },
        { label: "Actions", key: "actions" },
      ],
      rows: redeemedRows,
      renderRow: renderRedeemedRow,
      empty: "No redemptions found for the selected filters.",
      placeholder: "Search by Salesman / Customer / Code / Mobile / Gift",
    },
    salesmen: {
      columns: [
        { label: "Salesman", key: "salesman_name" },
        { label: "Customers", key: "customers_count" },
        { label: "Redemptions", key: "redemption_count" },
        { label: "QR Redeemed", key: "qr_redeemed" },
      ],
      rows: salesmanRows,
      renderRow: renderSalesmanRow,
      empty: "No salesmen found.",
      placeholder: "Search by Salesman",
    },
  };

  const customerTable = {
    columns: customerColumns,
    rows: customerRows,
    renderRow: renderCustomerRow,
    empty: "No customers found for the selected filters.",
    placeholder: "Search by Customer / Code / Mobile / Firm / City",
  };

  const table = tableConfig[cardType] || customerTable;

  const cards = [
    { type: "salesmen", label: "Total Salesmen", value: summary.salesmen },
    { type: "customers", label: "Total Customers", value: summary.customers },
    {
      type: "accumulated",
      label: "Total Accumulated",
      value: summary.accumulated,
    },
    {
      type: "redeemed",
      label: "Total Redeemed",
      value: summary.redeemed,
      sub: `${summary.gifts} gift(s)`,
    },
    {
      type: "scanned",
      label: "Total Redeemed & Accumulated  ",
      value: summary.scanned,
    },
  ];

  const goToCard = (type) => {
    setExpandedKey(null);
    navigate(`/dashboard/redeemedstatistcis/summary/${type}`, {
      state: { ...location.state, dateRange },
      replace: true,
    });
  };

  return (
    <DashboardLayout>
      <div className="max-w-full mx-auto px-2">
        <div className="flex items-center space-x-3 py-4">
          <button
            onClick={() => navigate(-1)}
            className="hover:text-gray-800"
            aria-label="Back"
          >
            <FaArrowLeft size={20} />
          </button>
          <h2 className="text-2xl font-bold text-amber-600 mb-1">
            Redemption Summary
          </h2>
        </div>

        {/* Filters: search + salesman */}
        <div className="w-full flex flex-col sm:flex-row items-center justify-start gap-4 mb-4">
          <SearchBar
            value={searchValue}
            onChange={setSearchValue}
            placeholder={table.placeholder}
          />
          <select
            value={selectedSalesman}
            onChange={(e) => setSelectedSalesman(e.target.value)}
            className="px-4 py-2 border border-gray-950 rounded-md text-sm bg-white"
            aria-label="Filter by salesman"
          >
            <option value="">All Salesmen</option>
            {salesmanOptions.map((s) => (
              <option key={s.user_id} value={s.user_id}>
                {s.name}
              </option>
            ))}
          </select>
          {(cardType === "redeemed" || cardType === "accumulated") && (
            <DatePicker
              selectsRange
              startDate={startDate}
              endDate={endDate}
              onChange={(update) => setDateRange(update)}
              isClearable
              placeholderText="Select date range"
              dateFormat="dd MMM yyyy"
              className="w-1.4 px-4 py-2 border border-gray-950 rounded-md text-sm placeholder-gray-950"
            />
          )}
        {(cardType === "scanned" || cardType === "accumulated" || cardType === "redeemed") && (
            <button
              onClick={handleExport}
              disabled={exporting}
              className="px-4 py-2 rounded-md text-sm font-medium bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
            >
              {exporting ? "Exporting..." : "Export"}
            </button>
          )}
        </div>

        {/* Summary cards — the active one is highlighted, the rest switch the list */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
          {cards.map((card) => {
            const active = card.type === cardType;
            return (
              <div
                key={card.type}
                onClick={() => goToCard(card.type)}
                className={`cursor-pointer rounded-xl border p-4 shadow-md transition-shadow duration-200 hover:shadow-lg ${
                  active
                    ? "border-amber-500 bg-amber-50 ring-2 ring-amber-400"
                    : "border-amber-500 bg-white"
                }`}
              >
                <p className="text-sm font-medium text-black">{card.label}</p>
                <p className="text-md font-bold text-black">
                  {card.value}
                  {card.sub && (
                    <>
                      {" - "}
                      <span className="text-md font-bold text-black">
                        {card.sub}
                      </span>
                    </>
                  )}
                </p>
              </div>
            );
          })}
        </div>

        {/* Table */}
        {loading ? (
          <p className="text-center text-gray-500">Loading ...</p>
        ) : error ? (
          <p className="text-center text-red-500">{error}</p>
        ) : table.rows.length === 0 ? (
          <p className="text-center text-gray-500 py-8">{table.empty}</p>
        ) : (
          <div className="overflow-x-auto rounded">
            <SortableTable
              columns={table.columns}
              data={table.rows}
              renderRow={table.renderRow}
            />
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default RedeemedSummary;
